import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import './Legal.css';

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const mail = <a href="mailto:contact@opteam-services.com">contact@opteam-services.com</a>;

  return (
    <div className="legal-page">
      <SEO
        title={isFr ? 'Politique de confidentialité' : 'Privacy Policy'}
        description={isFr
          ? 'Comment OPTEAM Services collecte, utilise et protège vos données personnelles, et comment exercer vos droits RGPD.'
          : 'How OPTEAM Services collects, uses and protects your personal data, and how to exercise your GDPR rights.'
        }
        canonical="/privacy-policy"
      />

      <section className="legal-hero">
        <div className="container">
          <h1>{isFr ? 'Politique de confidentialité' : 'Privacy Policy'}</h1>
          <p>{isFr ? 'Dernière mise à jour : 1er novembre 2026' : 'Last updated: November 1, 2026'}</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          {isFr ? (
            <div className="legal-body">
              <h2>Responsable du traitement</h2>
              <p>
                <strong>OPTEAM Services</strong>, SAS au capital de 50 000 €, RCS Nanterre 979 611 811,
                1 rue Marcel Allégot, 92190 Meudon, France. Contact : {mail}.
              </p>

              <h2>Données collectées</h2>
              <p>
                Nous collectons uniquement les données que vous nous transmettez. Via le formulaire de contact :
              </p>
              <ul>
                <li>nom et prénom ;</li>
                <li>adresse e-mail ;</li>
                <li>nom de votre entreprise (facultatif) ;</li>
                <li>contenu de votre message.</li>
              </ul>
              <p>
                Si vous réservez une démo, notre page de prise de rendez-vous Microsoft Bookings collecte en
                outre les informations que vous y saisissez (nom, e-mail et, si vous les renseignez, téléphone,
                adresse et précisions sur votre demande) ainsi que le créneau choisi.
              </p>
              <p>
                Si vous postulez à l'une de nos offres ou nous adressez une candidature spontanée depuis la page
                Carrières, nous collectons votre nom, votre e-mail, votre CV et, si vous les renseignez, votre
                téléphone, le lien de votre profil LinkedIn, le poste recherché et votre message. Pour une offre,
                nous collectons aussi vos réponses aux questions de présélection (expérience, langues, mobilité,
                autorisation de travail…). Ces réponses servent uniquement à étudier votre candidature : la
                décision est toujours prise par une personne de notre équipe. Nous conservons également les
                documents que vous nous transmettez ensuite au cours de nos échanges.
              </p>
              <p>
                Aucune donnée n'est collectée à votre insu et aucune décision automatisée n'est prise sur la base
                de ces informations.
              </p>

              <h2>Finalités et bases légales</h2>
              <ul>
                <li>
                  <strong>Répondre à votre demande, organiser la démo réservée</strong> et vous recontacter :
                  mesures précontractuelles prises à votre demande (article 6.1.b du RGPD).
                </li>
                <li>
                  <strong>Étudier votre candidature</strong> et vous recontacter : mesures
                  précontractuelles prises à votre demande (article 6.1.b du RGPD).
                </li>
                <li>
                  <strong>Assurer le suivi de la relation commerciale</strong> avec nos prospects et clients :
                  intérêt légitime d'OPTEAM Services (article 6.1.f du RGPD).
                </li>
              </ul>
              <p>Vos données ne sont jamais vendues ni cédées à des tiers à des fins commerciales.</p>

              <h2>Destinataires</h2>
              <p>
                Vos données sont accessibles uniquement aux membres habilités de l'équipe OPTEAM Services. Elles
                sont traitées par nos sous-traitants techniques, qui agissent sur nos instructions :
              </p>
              <ul>
                <li>
                  Google Ireland Limited (services Firebase et Google Cloud) pour les messages du formulaire de
                  contact, les candidatures et les CV ;
                </li>
                <li>
                  Microsoft Ireland Operations Limited (Microsoft 365 : Bookings, Outlook, Teams) pour les
                  réservations de démo et nos échanges par e-mail ou visioconférence.
                </li>
              </ul>

              <h2>Transferts hors de l'Union européenne</h2>
              <p>
                Google et Microsoft pouvant traiter certaines données hors de l'Union européenne, notamment aux
                États-Unis, ces transferts sont encadrés par les clauses contractuelles types de la Commission
                européenne et par le Data Privacy Framework UE–États-Unis, auquel Google LLC et Microsoft
                Corporation sont certifiées.
              </p>

              <h2>Durée de conservation</h2>
              <p>
                Les messages reçus via le formulaire de contact et les réservations de démo sont conservés
                <strong> 3 ans à compter de notre dernier échange</strong>, conformément aux recommandations de la CNIL pour les données de
                prospects. Si une relation contractuelle s'établit, les données sont conservées pendant la durée
                du contrat puis archivées selon les durées légales. Les candidatures, les CV et les documents
                associés sont conservés <strong> 2 ans au maximum</strong> après notre dernier échange, sauf si vous demandez leur
                suppression plus tôt.
              </p>

              <h2>Cookies et traceurs</h2>
              <p>
                Ce site n'utilise <strong>aucun cookie publicitaire ni de mesure d'audience</strong>. Seule votre
                préférence de langue est enregistrée dans le stockage local de votre navigateur ; ce stockage est
                strictement nécessaire au fonctionnement du site et ne requiert pas de consentement. Les polices
                de caractères sont hébergées sur nos propres serveurs. La page de réservation Microsoft Bookings
                s'ouvre dans un nouvel onglet, sur le site de Microsoft, qui applique sa propre politique en
                matière de cookies.
              </p>

              <h2>Vos droits</h2>
              <p>Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants :</p>
              <ul>
                <li>droit d'accès, de rectification et d'effacement de vos données ;</li>
                <li>droit à la limitation du traitement et droit à la portabilité ;</li>
                <li>droit d'opposition au traitement fondé sur notre intérêt légitime ;</li>
                <li>droit de définir des directives relatives au sort de vos données après votre décès.</li>
              </ul>
              <p>
                Pour exercer ces droits, écrivez-nous à {mail}. Nous vous répondrons dans un délai d'un mois.
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation
                auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>).
              </p>

              <h2>Sécurité</h2>
              <p>
                Les échanges avec le site sont chiffrés (HTTPS) et l'accès aux messages reçus est réservé aux
                personnes habilitées, via une authentification sécurisée.
              </p>

              <h2>Modification de la politique</h2>
              <p>
                Cette politique peut être mise à jour pour refléter l'évolution du site ou de la réglementation.
                La date de dernière mise à jour figure en haut de cette page.
              </p>
            </div>
          ) : (
            <div className="legal-body">
              <h2>Data controller</h2>
              <p>
                <strong>OPTEAM Services</strong>, SAS with a share capital of €50,000, Nanterre Trade Register
                979 611 811, 1 rue Marcel Allégot, 92190 Meudon, France. Contact: {mail}.
              </p>

              <h2>Data we collect</h2>
              <p>We only collect the data you send us. Through the contact form:</p>
              <ul>
                <li>first and last name;</li>
                <li>email address;</li>
                <li>company name (optional);</li>
                <li>the content of your message.</li>
              </ul>
              <p>
                If you book a demo, our Microsoft Bookings scheduling page also collects the information you enter
                there (name, email and, if provided, phone number, address and details about your request) as well
                as the time slot you choose.
              </p>
              <p>
                If you apply for one of our offers or send us a spontaneous application from the Careers page, we
                collect your name, email, resume and, if provided, your phone number, LinkedIn profile link,
                desired position and message. For an offer, we also collect your answers to the screening questions
                (experience, languages, mobility, work authorization…). These answers are only used to review your
                application: the decision is always made by a member of our team. We also keep any documents you
                send us later in our exchanges.
              </p>
              <p>
                No data is collected without your knowledge and no automated decision is made based on this
                information.
              </p>

              <h2>Purposes and legal bases</h2>
              <ul>
                <li>
                  <strong>Answering your request, organising the demo you booked</strong> and getting back to you:
                  pre-contractual steps taken at your request (Article 6(1)(b) GDPR).
                </li>
                <li>
                  <strong>Reviewing your application</strong> and getting back to you: pre-contractual
                  steps taken at your request (Article 6(1)(b) GDPR).
                </li>
                <li>
                  <strong>Managing our business relationship</strong> with prospects and clients: legitimate
                  interest of OPTEAM Services (Article 6(1)(f) GDPR).
                </li>
              </ul>
              <p>Your data is never sold or shared with third parties for marketing purposes.</p>

              <h2>Recipients</h2>
              <p>
                Your data is only accessible to authorised members of the OPTEAM Services team. It is processed by
                our technical processors, which act on our instructions:
              </p>
              <ul>
                <li>
                  Google Ireland Limited (Firebase and Google Cloud services) for contact form messages and
                  job applications and resumes;
                </li>
                <li>
                  Microsoft Ireland Operations Limited (Microsoft 365: Bookings, Outlook, Teams) for demo bookings
                  and our email or video exchanges.
                </li>
              </ul>

              <h2>Transfers outside the European Union</h2>
              <p>
                As Google and Microsoft may process some data outside the European Union, in particular in the
                United States, such transfers are governed by the European Commission's standard contractual
                clauses and by the EU–US Data Privacy Framework, under which Google LLC and Microsoft Corporation
                are certified.
              </p>

              <h2>Retention period</h2>
              <p>
                Messages received through the contact form and demo bookings are kept for <strong>3 years from
                our last exchange</strong>, in line with the French data protection authority (CNIL) recommendations for
                prospect data. If a contractual relationship is established, data is kept for the duration of the
                contract and then archived for the legally required periods. Applications, resumes and related documents
                are kept for <strong> 2 years at most</strong> after our last exchange, unless you ask us to delete them sooner.
              </p>

              <h2>Cookies and trackers</h2>
              <p>
                This website uses <strong>no advertising or audience measurement cookies</strong>. Only your
                language preference is saved in your browser's local storage; this is strictly necessary for the
                website to work and does not require consent. Fonts are served from our own servers. The Microsoft
                Bookings scheduling page opens in a new tab on Microsoft's website, which applies its own cookie
                policy.
              </p>

              <h2>Your rights</h2>
              <p>Under the GDPR and the French Data Protection Act, you have the following rights:</p>
              <ul>
                <li>right of access, rectification and erasure of your data;</li>
                <li>right to restriction of processing and right to data portability;</li>
                <li>right to object to processing based on our legitimate interest;</li>
                <li>right to set instructions regarding your data after your death.</li>
              </ul>
              <p>
                To exercise these rights, write to us at {mail}. We will reply within one month. If you believe
                your rights have not been respected, you may lodge a complaint with the CNIL
                (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>).
              </p>

              <h2>Security</h2>
              <p>
                Communications with the website are encrypted (HTTPS) and access to received messages is
                restricted to authorised people through secure authentication.
              </p>

              <h2>Changes to this policy</h2>
              <p>
                This policy may be updated to reflect changes to the website or to regulations. The date of the
                last update is shown at the top of this page.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
